export type BluefinFrameCheck =
    | { status: "unknown" }
    | { status: "complete" }
    | { status: "incomplete"; received: number; expected: number };

const FRAME_HEADER = /^02[0-9A-Fa-f]{4}/;
const FRAME_ENVELOPE_LENGTH = 12;

/**
 * Advisory integrity check for the ID TECH SREDKey keyboard-wedge frames that
 * Bluefin card readers type into the payment-data field.
 *
 * The wedge envelope is <STX><DataLenL><DataLenH><Card Data><CheckLRC>
 * <CheckSum><ETX> (ID TECH Encrypted Data Output Formats, P/N 80000502-001).
 * In keyboard mode that renders as "02", four hex length characters, the card
 * data, and a six-character trailer ending in "03" — so a complete frame is
 * always exactly (declared length + 12) characters, in every entry mode. The
 * frame is checked only against its own header, never against assumptions
 * about mode or firmware. Dropped wedge keystrokes (#35361) shorten the frame
 * while leaving the declared length intact, which makes them detectable here
 * before the payload is burned on a CyberSource 101 decline.
 *
 * The result is advisory only and must never block submission: input that
 * doesn't look like this frame family gets no opinion ("unknown").
 */
export const checkBluefinFrame = (raw: string): BluefinFrameCheck => {
    const frame = raw.trim();
    if (!FRAME_HEADER.test(frame)) {
        return { status: "unknown" };
    }
    const declared = parseInt(frame.slice(4, 6) + frame.slice(2, 4), 16);
    const expected = declared + FRAME_ENVELOPE_LENGTH;
    if (frame.length === expected && frame.endsWith("03")) {
        return { status: "complete" };
    }
    return { status: "incomplete", received: frame.length, expected };
};
